package com.rajen.angrejidictionary.analytics

import kotlinx.browser.window

/**
 * Thin wrapper around the Umami `window.umami` tracker script.
 * Every public function maps to a distinct, named event so behaviour
 * on the site can be measured accurately from the Umami dashboard.
 *
 * Calls are safe no-ops if the script hasn't loaded (dev mode, ad-blockers)
 * because of the `?.` safe call.
 */
object Umami {

    /** Fire a simple named event with no extra data. */
    fun track(event: String) {
        window.asDynamic().umami?.track(event)
        GoogleAnalytics.event(event)
    }

    private fun track(event: String, data: dynamic) {
        window.asDynamic().umami?.track(event, data)
        GoogleAnalytics.event(event, data)
    }

    // ── Word of the day / lookup ────────────────────────────────────

    fun trackWordView(word: String) {
        val data = js("{}")
        data.word = word
        track("word-view", data)
    }

    fun trackWordSearch(term: String) {
        val data = js("{}")
        data.term = term
        track("word-search", data)
    }

    fun trackWordNotFound(term: String) {
        val data = js("{}")
        data.term = term
        track("word-not-found", data)
    }

    fun trackWordLoadError(reason: String) {
        val data = js("{}")
        data.reason = reason
        track("word-load-error", data)
    }

    fun trackWordTabSelect(tab: String) {
        val data = js("{}")
        data.tab = tab
        track("word-tab-select", data)
    }

    // ── Navigation / chrome ──────────────────────────────────────────

    fun trackLogoClick() {
        track("logo-click")
    }

    fun trackNavClick(destination: String, source: String) {
        val data = js("{}")
        data.destination = destination
        data.source = source
        track("nav-click", data)
    }

    fun trackSocialClick(platform: String) {
        val data = js("{}")
        data.platform = platform
        track("social-click", data)
    }

    // ── Contact form ───────────────────────────────────────────────

    fun trackContactSubmit() {
        track("contact-submit")
    }

    fun trackContactSuccess() {
        track("contact-success")
    }

    fun trackContactError(reason: String) {
        val data = js("{}")
        data.reason = reason
        track("contact-error", data)
    }

    // ── Admin: auth ──────────────────────────────────────────────────

    fun trackAdminLoginAttempt() {
        track("admin-login-attempt")
    }

    fun trackAdminLoginSuccess(username: String) {
        val data = js("{}")
        data.username = username
        track("admin-login-success", data)
    }

    fun trackAdminLoginError(reason: String) {
        val data = js("{}")
        data.reason = reason
        track("admin-login-error", data)
    }

    fun trackAdminLogout() {
        track("admin-logout")
    }

    // ── Admin: dashboard ───────────────────────────────────────────

    fun trackAdminTabSelect(tab: String) {
        val data = js("{}")
        data.tab = tab
        track("admin-tab-select", data)
    }

    fun trackAdminWordParse(success: Boolean, reason: String? = null) {
        val data = js("{}")
        data.success = success
        if (reason != null) data.reason = reason
        track("admin-word-parse", data)
    }

    fun trackAdminWordSubmit(success: Boolean, word: String? = null, reason: String? = null) {
        val data = js("{}")
        data.success = success
        if (word != null) data.word = word
        if (reason != null) data.reason = reason
        track("admin-word-submit", data)
    }

    fun trackAdminWordsPageChange(page: Int) {
        val data = js("{}")
        data.page = page
        track("admin-words-page", data)
    }

    fun trackAdminWordSearch(term: String) {
        val data = js("{}")
        data.term = term
        track("admin-word-search", data)
    }

    fun trackAdminWordEditStart(wordId: String) {
        val data = js("{}")
        data.wordId = wordId
        track("admin-word-edit-start", data)
    }

    fun trackAdminWordPreviewClick(wordId: String) {
        val data = js("{}")
        data.wordId = wordId
        track("admin-word-preview-click", data)
    }

    fun trackAdminWordDelete(success: Boolean, wordId: String, reason: String? = null) {
        val data = js("{}")
        data.success = success
        data.wordId = wordId
        if (reason != null) data.reason = reason
        track("admin-word-delete", data)
    }

    fun trackAdminWordOfDayPin(success: Boolean, wordId: String?, reason: String? = null) {
        val data = js("{}")
        data.success = success
        data.wordId = wordId ?: "none"
        if (reason != null) data.reason = reason
        track("admin-word-of-day-pin", data)
    }

    // ── Admin: suggestions ─────────────────────────────────────────

    fun trackAdminSuggestionsView(page: Int, status: String?) {
        val data = js("{}")
        data.page = page
        data.status = status ?: "all"
        track("admin-suggestions-view", data)
    }

    fun trackAdminSuggestionAction(action: String, suggestionId: String) {
        val data = js("{}")
        data.action = action
        data.suggestionId = suggestionId
        track("admin-suggestion-action", data)
    }

    // ── Admin: notifications ─────────────────────────────────────────

    fun trackAdminBroadcastAttempt(hasDescription: Boolean, hasImage: Boolean, hasWord: Boolean) {
        val data = js("{}")
        data.hasDescription = hasDescription
        data.hasImage = hasImage
        data.hasWord = hasWord
        track("admin-broadcast-attempt", data)
    }

    fun trackAdminBroadcastWordSelect(word: String) {
        val data = js("{}")
        data.word = word
        track("admin-broadcast-word-select", data)
    }

    fun trackAdminBroadcastSuccess(successCount: Long, failureCount: Long, prunedTokens: Long) {
        val data = js("{}")
        data.successCount = successCount
        data.failureCount = failureCount
        data.prunedTokens = prunedTokens
        track("admin-broadcast-success", data)
    }

    fun trackAdminBroadcastError(reason: String) {
        val data = js("{}")
        data.reason = reason
        track("admin-broadcast-error", data)
    }
}